import React from "react";
import { Card, CardContent, Typography, Grid, Box, alpha, useTheme } from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  SportsScore as MatchIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Groups as GroupsIcon,
  MilitaryTech as MilitaryTechIcon,
} from "@mui/icons-material";
import { useOrganization } from "../contexts/OrganizationContext";
import { TournamentAggregate } from "../hooks/useTournamentStats";

interface TournamentAggregateStatsProps {
  aggregate: TournamentAggregate;
}

const PE_TILE = {
  bg: "#FAFAFA",
  border: "#D8C4C1",
  accent: "#743444",
  iconBg: "#F4C48B",
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  caption?: string;
  color: string;
  isPE?: boolean;
}> = ({ icon, value, label, caption, color: rawColor, isPE }) => {
  const color = isPE ? PE_TILE.accent : rawColor;
  return (
  <Grid item xs={12} sm={6} md={4} lg={2}>
    <Box
      sx={{
        p: 2,
        borderRadius: isPE ? "16px" : 3,
        height: "100%",
        bgcolor: isPE ? PE_TILE.bg : alpha(color, 0.07),
        border: "1px solid",
        borderColor: isPE ? PE_TILE.border : alpha(color, 0.2),
        ...(!isPE && {
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
        }),
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: isPE ? PE_TILE.iconBg : alpha(color, 0.15),
          color,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: "bold", color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  </Grid>
  );
};

const TournamentAggregateStats: React.FC<TournamentAggregateStatsProps> = ({
  aggregate,
}) => {
  const theme = useTheme();
  const { currentOrg } = useOrganization();
  const isPE = currentOrg?.slug === "padel-eagle";
  const gameDiffColor =
    aggregate.gameDiff >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Card sx={{ borderRadius: isPE ? "16px" : 3, ...(isPE && { boxShadow: "none", border: "1px solid #D8C4C1" }) }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Career Stats
        </Typography>
        <Grid container spacing={2}>
          <StatCard
            isPE={isPE}
            icon={<TrophyIcon />}
            value={aggregate.tournamentsPlayed}
            label="Tournaments Played"
            color={theme.palette.primary.main}
          />
          <StatCard
            isPE={isPE}
            icon={<MatchIcon />}
            value={aggregate.totalMatches}
            label="Matches Played"
            color={theme.palette.info.main}
          />
          <StatCard
            isPE={isPE}
            icon={<TrendingUpIcon />}
            value={`${Math.round(aggregate.winRate * 100)}%`}
            label="Win Rate"
            caption={`${aggregate.wins}W - ${aggregate.losses}L`}
            color={theme.palette.success.main}
          />
          <StatCard
            isPE={isPE}
            icon={<CompareArrowsIcon />}
            value={aggregate.gameDiff >= 0 ? `+${aggregate.gameDiff}` : aggregate.gameDiff}
            label="Game Diff"
            caption={`${aggregate.gamesWon} won - ${aggregate.gamesLost} lost`}
            color={gameDiffColor}
          />
          <StatCard
            isPE={isPE}
            icon={<GroupsIcon />}
            value={
              aggregate.groupStage.matches > 0
                ? `${Math.round(aggregate.groupStage.winRate * 100)}%`
                : "—"
            }
            label="Group Stage Win Rate"
            caption={`${aggregate.groupStage.wins}W - ${aggregate.groupStage.losses}L`}
            color={theme.palette.secondary.main}
          />
          <StatCard
            isPE={isPE}
            icon={<MilitaryTechIcon />}
            value={
              aggregate.knockout.matches > 0
                ? `${Math.round(aggregate.knockout.winRate * 100)}%`
                : "—"
            }
            label="Knockout Win Rate"
            caption={`${aggregate.knockout.wins}W - ${aggregate.knockout.losses}L`}
            color={theme.palette.warning.main}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TournamentAggregateStats;
